<script setup lang="ts" generic="TRow">
import { ElTableV2, ElAutoResizer } from 'element-plus'

import type { VirtualTableColumn } from './types'

// The wrapper below is not the attribute target — every attribute a caller passes is
// meant for the grid, and `v-bind="$attrs"` puts it there.
defineOptions({ inheritAttrs: false })

/**
 * A table that renders only the rows in view.
 *
 * For a list whose rows are expensive to build — several controls on each — where `Table`
 * pays for every row the page holds before it can paint. The grid asks for a width and a
 * height in pixels rather than growing with its content, so the table lives inside a
 * scroll area of a stated height and `ElAutoResizer` measures it.
 *
 * Cells come from one `cell` slot rather than from a column each: the grid renders columns
 * from data, so what a cell holds is decided by the column it belongs to. The expand
 * triangle and the tree indent are drawn around that slot by the grid itself.
 *
 * Rows nest through `children` on a row plus `expandColumnKey` — the tree is flattened
 * before it is virtualized, so a group with a thousand rows under it costs what a group
 * with ten does.
 */
withDefaults(
  defineProps<{
    columns: VirtualTableColumn[]
    data: TRow[]
    /** The row field holding a key unique across the whole list, nested rows included. */
    rowKey: string
    /** The scroll area's height in px — the grid cannot work it out from its content. */
    height?: number
    /**
     * Roughly how tall a row is. Given, rows are measured instead of assumed, which is
     * what a row whose height depends on its content needs.
     */
    estimatedRowHeight?: number
    /** The column the expand triangle sits in; unset renders the list flat. */
    expandColumnKey?: string
    /**
     * Which rows start open. Only *start*: the grid takes a copy at setup and owns the
     * state from there — folding a group by hand is its business, not the caller's, and
     * writing this later changes nothing. Remount the table (`:key`) to open a new set.
     */
    defaultExpandedRowKeys?: string[]
    loading?: boolean
  }>(),
  { height: 460 }
)

/** What `Table` gives its header, so the two read as the same table. */
const HEADER_HEIGHT = 40

defineSlots<{
  /**
   * One cell. The grid renders columns from data, so the payload says which column is being
   * drawn and which row it belongs to. The grid passes more (`cellData`, the indices); add
   * one to this type when something actually reads it, rather than declaring a shape nobody
   * has verified.
   */
  cell?: (props: { column: VirtualTableColumn; rowData: TRow }) => unknown
  empty?: () => unknown
}>()
</script>

<template>
  <div
    v-loading="loading"
    class="virtual-table"
    :style="{ height: `${height}px` }"
  >
    <ElAutoResizer>
      <template #default="{ height: areaHeight, width }">
        <ElTableV2
          :columns="columns"
          :data="data"
          :row-key="rowKey"
          :width="width"
          :height="areaHeight"
          :estimated-row-height="estimatedRowHeight"
          :header-height="HEADER_HEIGHT"
          :expand-column-key="expandColumnKey"
          :default-expanded-row-keys="defaultExpandedRowKeys"
          v-bind="$attrs"
        >
          <template #cell="cellProps">
            <slot name="cell" v-bind="cellProps" />
          </template>

          <template #empty>
            <slot name="empty" />
          </template>
        </ElTableV2>
      </template>
    </ElAutoResizer>
  </div>
</template>

<style lang="scss" scoped>
.virtual-table {
  width: 100%;
}

// The grid is styled to match `Table`, which is what every other list on the screen is:
// same 12px text inset, same rules between rows, same muted header, same hover. Element
// Plus ships the two components with visibly different defaults — a heavier header, no
// rule under it, a tighter inset — and side by side that reads as two kinds of table.
.virtual-table :deep(.el-table-v2__header-cell) {
  padding: 0 12px;
  font-weight: 600;
  color: var(--el-text-color-secondary);
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.virtual-table :deep(.el-table-v2__row-cell) {
  padding: 0 12px;
  color: var(--el-text-color-regular);
  // `Table`'s row height, stated on the cell rather than the row: the grid measures a
  // row to place the next one, so a height forced on the row itself would be taller than
  // the position it was given and the rows would overlap.
  min-height: 49px;
}

.virtual-table :deep(.el-table-v2__row:hover) {
  background-color: var(--el-fill-color-light);
}

// A cell is laid out by the grid and clips by default, which cuts a row carrying controls
// down to its first line. Its content decides its own height instead, and the row is
// measured around it.
.virtual-table :deep(.el-table-v2__row-cell) {
  align-items: center;
  overflow: visible;
}

.virtual-table :deep(.el-table-v2__row) {
  align-items: stretch;
}
</style>
